import {useEffect, useId, useMemo, useState} from "react";
import {Button} from "@/primitives/Button";
import {createDataTableColumnHelper, DataTable, useDataTable} from "@/primitives/DataTable";
import {DateRange} from "@/components/DateRange";
import {api} from "@/shared/api";
import type {WorkingTimeNotice} from "@/shared/timr";
import {formatDateTime, parseInstant, parseISODate} from "@/shared/utils/datefunctions";
import {Temporal} from "@js-temporal/polyfill";

interface WorkingTimeNoticeRevisionsProps {
	noticeId: number;
}

const columnHelper = createDataTableColumnHelper<WorkingTimeNotice>();

/**
 * The notice dialog's "history" section: a collapsed toggle that reveals the notice's audit revisions,
 * newest first — mirroring the history of the request dialogs (see {@code workingTimeRequestDialog.jsp}
 * and {@code workingTimeDateSpanRevisionTable.jsp}).
 */
export function WorkingTimeNoticeRevisions({noticeId}: WorkingTimeNoticeRevisionsProps) {
	const [expanded, setExpanded] = useState(false);
	const [revisions, setRevisions] = useState<WorkingTimeNotice[]>([]);
	const tableId = useId();

	useEffect(() => {
		// The dialog remounts on every open (via the `key` on the dialog instance), so the history is refetched each time it opens.
		const request = api("/notices/findWorkingTimeNoticeRevisions.json", true)
			.get({id: noticeId})
			.done((data: WorkingTimeNotice[]) => setRevisions(data ?? []));

		return () => request.abort();
	}, [noticeId]);

	const columns = useMemo(
		() =>
			columnHelper.columns([
				columnHelper.accessor("lastModified", {
					header: messages["report.column.lastModified"],
					/* The wire value is a UTC instant, rendered in the viewer's zone. */
					cell: ({getValue}) => formatDateTime(parseInstant(getValue())?.toZonedDateTimeISO(Temporal.Now.timeZoneId())),
				}),
				columnHelper.accessor("modifiedByUser", {
					header: messages["report.column.modifiedByUser"],
				}),
				columnHelper.accessor("workingTimeTypeName", {
					header: messages["workingTimeNotice.workingTimeType"],
				}),
				columnHelper.display({
					id: "range",
					header: messages["report.column.range"],
					cell: ({row}) => <DateRange start={parseISODate(row.original.start)} end={parseISODate(row.original.end)}/>,
				}),
				columnHelper.accessor("description", {
					header: messages["workingTimeNotice.description"],
					meta: {cellClassName: "table-data-description"},
				}),
			]),
		[],
	);

	/* Served newest first and not sortable, like the revision tables of the other dialogs. The revisions are
	   immutable, so the position is a stable row id. */
	const table = useDataTable<WorkingTimeNotice>({
		data: revisions,
		columns,
		enableSorting: false,
		getRowId: (_, index) => String(index),
	});

	if (revisions.length === 0) {
		return null;
	}

	return (
		<div className="mt-10">
			<Button
				variant="link"
				className="p-0"
				aria-expanded={expanded}
				aria-controls={tableId}
				onClick={() => setExpanded(previous => !previous)}
			>
				<i className={`far position-left ${expanded ? "fa-angle-up" : "fa-angle-down"}`}/>
				{messages[expanded ? "dialog.revision.collapse.hide" : "dialog.revision.collapse.show"]}
			</Button>
			{expanded && (
				<DataTable.Root id={tableId} table={table} className="mt-5">
					<DataTable.Content<WorkingTimeNotice> size="xs" striped hover containerClassName="table-overflow-visible"/>
				</DataTable.Root>
			)}
		</div>
	);
}
